// Shared, tz-local time formatting for event cards + forms, plus the one
// definition of what shape a week is.

import {
  addHours,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

import type { EventRow } from '../types'

// How long an open-ended event (no end_time) is assumed to run past its start
// before it counts as over. Andrew's chosen default for listings that never
// gave us a close time (2026-08-20): long enough that a 9p karaoke night isn't
// gone at 9:05, short enough that a no-end event stops holding a spot on
// today's view once it's clearly wrapped.
const OPEN_ENDED_HOURS = 2.5

// ---- Over, or still to come ----------------------------------------------
//
// The single definition of "past", used by the show-past-events toggle.
//
// An event is past once it has ENDED, not once it started. 156 of the 452
// approved rows carry no end_time, and those are where the difference bites:
// karaoke listed at 9p with no end is not over at 9:05, and hiding it then
// would take an event off the calendar while people are still walking in. So a
// missing end_time reads as "runs until OPEN_ENDED_HOURS after its start" -
// we don't know when it finishes, and the honest default is to keep showing
// it for a while rather than declare it over early, but not forever, since an
// 11am event with no end shouldn't still be "on" at midnight. Same instinct
// as a null cost_type printing no price: when we don't know, show it and say
// nothing, never guess it away - we're just bounding the guess now instead of
// leaving it open-ended for the rest of the day.
export function isPastEvent(
  e: Pick<EventRow, 'start_time' | 'end_time'>,
  now: Date,
): boolean {
  const ends = e.end_time
    ? new Date(e.end_time)
    : addHours(new Date(e.start_time), OPEN_ENDED_HOURS)
  return ends.getTime() < now.getTime()
}

// ---- Week shape ----------------------------------------------------------
//
// Weeks start MONDAY (Andrew, 7/28) so Saturday and Sunday sit next to each
// other. "What am I doing this weekend" is the question both the month grid and
// the mobile day strip get asked, and a Sunday-start week splits the answer
// across opposite ends of the row.
//
// Everything that lays out a week reads WEEK_OPTS, and the weekday headings are
// DERIVED from it rather than typed out. That is the whole point: a hardcoded
// ['Mon', ...] list that disagrees with the grid math is the silent version of
// this bug - every cell shifts by one, nothing throws, and you find out when an
// event turns up on the wrong day.
export const WEEK_OPTS = { weekStartsOn: 1 } as const

// Any date works; this renders one whole week and reads its labels off it.
export const WEEKDAY_LABELS: string[] = eachDayOfInterval({
  start: startOfWeek(new Date(2026, 0, 5), WEEK_OPTS),
  end: endOfWeek(new Date(2026, 0, 5), WEEK_OPTS),
}).map((d) => format(d, 'EEE'))

/** The seven days of the week containing `d`. */
export function weekDays(d: Date): Date[] {
  return eachDayOfInterval({
    start: startOfWeek(d, WEEK_OPTS),
    end: endOfWeek(d, WEEK_OPTS),
  })
}

/**
 * The full block a month grid draws: whole weeks covering `anchor`'s month, so
 * the first and last rows spill into the neighbouring months. Always a multiple
 * of 7 days, which is what lets the grid set its row count by dividing by 7.
 */
export function monthGridRange(anchor: Date): { start: Date; end: Date } {
  return {
    start: startOfWeek(startOfMonth(anchor), WEEK_OPTS),
    end: endOfWeek(endOfMonth(anchor), WEEK_OPTS),
  }
}

export function fmtTime(iso: string): string {
  const d = new Date(iso)
  let h = d.getHours()
  const m = d.getMinutes()
  const ap = h >= 12 ? 'p' : 'a'
  h = h % 12
  if (h === 0) h = 12
  return m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, '0')}${ap}`
}

// "7–9p" style compact range for a card (end optional).
export function fmtRange(startIso: string, endIso: string | null): string {
  if (!endIso) return fmtTime(startIso)
  return `${fmtTime(startIso)}–${fmtTime(endIso)}`
}
